import React, { useEffect, useState } from "react";
import Spinner from "../uiComponents/Spinner";
import { AuthUser } from "../../types/auth-types";
import { useAuthApi } from "../../api/useAuthApi";
import { useNavigation } from "../../hooks/useNavigation";
import { AppRoute } from "../../types/routes";
import { supabase } from "../../supabaseClient";
import ResetPassword from "./ResetPassword";

import { trackEvent } from "../../utils/firebase-analytics";
import { completeSignup, GOOGLE_SIGNUP_KEY } from "../../utils/signup-analytics";

type CallbackState = "loading" | "recovery" | "error";

function parseHashParams(hash: string): Record<string, string> {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const params: Record<string, string> = {};
  for (const pair of raw.split("&")) {
    const [key, value] = pair.split("=");
    if (key) params[decodeURIComponent(key)] = decodeURIComponent(value ?? "");
  }
  return params;
}

const AuthCallback: React.FC<{ onAuthSuccess: (user: AuthUser) => void }> = ({
  onAuthSuccess,
}) => {
  const [state, setState] = useState<CallbackState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const { fetchMe } = useAuthApi();
  const { replace } = useNavigation();

  useEffect(() => {
    const handle = async () => {
      const hash = window.location.hash;
      const params = parseHashParams(hash);

      if (params.access_token && params.type === "recovery") {
        const { error } = await supabase.auth.setSession({
          access_token: params.access_token,
          refresh_token: params.refresh_token || "",
        });

        if (error) {
          setErrorMessage("Recovery link is invalid or expired.");
          setState("error");
          return;
        }

        window.history.replaceState(null, "", window.location.pathname);
        setState("recovery");
        return;
      }

      // Google OAuth flow: backend already set HTTP-only cookies on the redirect.
      // Call fetchMe to verify the session and complete login.
      const me = await fetchMe();
      const userData = me?.data?.user;

      let signupEntry = false;
      try {
        const pending = sessionStorage.getItem(GOOGLE_SIGNUP_KEY);
        signupEntry = Boolean(pending && Date.now() - Number(pending) < 60 * 60 * 1000);
        sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);
      } catch {}

      if (userData) {
        if (signupEntry) completeSignup(userData.id, "google");
        onAuthSuccess({
          id: userData.id,
          email: userData.email,
          app_metadata: {},
          user_metadata: {},
          aud: "authenticated",
          created_at: new Date().toISOString(),
        });
        return;
      }

      if (signupEntry) trackEvent("signup_failed", { method: "google", stage: "oauth_callback" });
      setErrorMessage("Authentication failed. Please try again.");
      setState("error");
    };

    handle();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "recovery") {
    return <ResetPassword />;
  }

  if (state === "error") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div style={{ color: "#EF4444", fontSize: "16px", fontWeight: 600 }}>
          {errorMessage}
        </div>
        <button
          onClick={() => replace(AppRoute.LOGIN)}
          style={{
            background: "#4f46e5",
            color: "white",
            border: "none",
            borderRadius: "24px",
            padding: "10px 24px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Spinner size="large" />
      <div style={{ color: "#6B7280", fontSize: "14px" }}>
        Completing secure login...
      </div>
    </div>
  );
};

export default AuthCallback;
